import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';
import { 
  Shield, ChevronLeft, AlertTriangle, ExternalLink, Calendar, Clock, User,
  Target, DollarSign, FileText, Code, Link2, CheckCircle, XCircle, AlertCircle,
  Activity, Server, Layers, Tag, Sparkles, RefreshCw, Download, Copy, 
  BookOpen, Bug, Zap, Globe, Lock, FileWarning, Timer, TrendingUp, Clipboard,
  AlertOctagon, Wrench, Info, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Severity color mapping
const getSeverityColor = (severity) => {
  const colors = {
    critical: 'bg-red-500/20 text-red-400 border-red-500/30',
    high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    low: 'bg-green-500/20 text-green-400 border-green-500/30',
    info: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  };
  return colors[severity] || colors.info;
};

// Status color mapping
const getStatusColor = (status) => {
  const colors = {
    open: 'bg-red-500/20 text-red-400 border-red-500/30',
    closed: 'bg-green-500/20 text-green-400 border-green-500/30',
    under_remediation: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    in_progress: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    resolved: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    false_positive: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
    accepted_risk: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    pending_review: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    approved_exception: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  };
  return colors[status] || colors.open;
};

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return 'N/A';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

// Assessment type labels
const ASSESSMENT_TYPE_LABELS = {
  web: 'Web Application',
  api: 'API Security',
  network: 'Network Scan',
  mobile: 'Mobile App',
  infrastructure: 'Infrastructure',
  cloud: 'Cloud Security',
  pentest: 'Penetration Test',
  raptor: 'Raptor AI Pentest',
  compliance: 'Compliance Scan',
  vulnerability: 'Vulnerability Scan',
  code_scan: 'Code Scan',
  manual: 'Manual Entry',
};

const getAssessmentTypeLabel = (type) => {
  if (!type) return 'Unknown';
  return ASSESSMENT_TYPE_LABELS[type] || type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ');
};

// Format status
const formatStatus = (status) => {
  if (!status) return 'Unknown';
  return status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

export default function VulnerabilityDetail() {
  const { vulnId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [vuln, setVuln] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchVulnerability();
  }, [vulnId]);

  const fetchVulnerability = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/vulnerabilities/${vulnId}`);
      setVuln(res.data);
    } catch (error) {
      toast.error('Failed to fetch vulnerability details');
      navigate('/vulnerability-hub');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  if (!vuln) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <AlertTriangle className="w-12 h-12 text-yellow-400 mb-4" />
        <p className={`text-lg ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Vulnerability not found</p>
        <Button onClick={() => navigate('/vulnerability-hub')} className="mt-4">
          Back to Vulnerability Hub
        </Button>
      </div>
    );
  }

  // Calculate SLA status
  const calculateSlaStatus = () => {
    if (!vuln.discovered_at) return { status: 'unknown', daysRemaining: null };
    
    const discoveredDate = new Date(vuln.discovered_at);
    const slaHours = {
      critical: 24, high: 168, medium: 720, low: 2160
    }[vuln.severity] || 720;
    
    const deadline = new Date(discoveredDate.getTime() + slaHours * 60 * 60 * 1000);
    const now = new Date();
    const msRemaining = deadline.getTime() - now.getTime();
    const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
    
    if (vuln.status === 'closed') return { status: 'fixed', daysRemaining: 0 };
    if (msRemaining <= 0) return { status: 'overdue', daysRemaining };
    if (msRemaining <= 24 * 60 * 60 * 1000) return { status: 'critical', daysRemaining };
    return { status: 'on_track', daysRemaining };
  };

  const slaStatus = calculateSlaStatus();
  const assessmentType = vuln.assessment_type || vuln.scan_type || vuln.source;
  const assessmentName = vuln.assessment_name || getAssessmentTypeLabel(assessmentType);

  // Build reference links
  const references = [
    vuln.cve_id && { url: `https://nvd.nist.gov/vuln/detail/${vuln.cve_id}`, source: 'NVD', name: 'National Vulnerability Database' },
    vuln.cve_id && { url: `https://cve.mitre.org/cgi-bin/cvename.cgi?name=${vuln.cve_id}`, source: 'MITRE', name: 'MITRE CVE Record' },
    vuln.cwe && { url: `https://cwe.mitre.org/data/definitions/${vuln.cwe.replace('CWE-', '')}.html`, source: 'CWE', name: 'CWE Definition' },
  ].filter(Boolean);

  return (
    <div data-testid="vulnerability-detail-page" className="space-y-5 pb-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate('/vulnerability-hub')}
            className={`${isDarkMode ? 'text-zinc-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
            data-testid="back-to-hub-btn"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back
          </Button>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            vuln.severity === 'critical' ? 'bg-red-500/15 border border-red-500/30' :
            vuln.severity === 'high' ? 'bg-orange-500/15 border border-orange-500/30' :
            vuln.severity === 'medium' ? 'bg-yellow-500/15 border border-yellow-500/30' :
            'bg-green-500/15 border border-green-500/30'
          }`}>
            <Bug className={`w-5 h-5 ${
              vuln.severity === 'critical' ? 'text-red-400' :
              vuln.severity === 'high' ? 'text-orange-400' :
              vuln.severity === 'medium' ? 'text-yellow-400' : 'text-green-400'
            }`} />
          </div>
          <div>
            <h1 className={`font-inter font-bold text-xl lg:text-2xl tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Vulnerability Details
            </h1>
            <p className={`font-inter text-sm mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              ID: {vuln.id?.substring(0, 8)}...
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={() => navigate(`/ai-insights/${vuln.id}`)}
            className="bg-violet-600 hover:bg-violet-500"
            data-testid="ai-insights-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            AI Insights
          </Button>
          <Button
            onClick={() => navigate(`/ai-remediation/${vuln.id}`)}
            className="bg-emerald-600 hover:bg-emerald-500"
            data-testid="ai-remediation-btn"
          >
            <Shield className="w-4 h-4 mr-2" />
            AI Remediation
          </Button>
        </div>
      </div>

      {/* Title and Key Info Card */}
      <div className={`rounded-2xl p-5 lg:p-6 ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200 shadow-card'}`}>
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-4">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Badge className={`${getSeverityColor(vuln.severity)} text-sm uppercase font-bold`}>
                {vuln.severity}
              </Badge>
              <Badge className={`${getStatusColor(vuln.status)} text-sm`}>
                {formatStatus(vuln.status)}
              </Badge>
              {assessmentType && (
                <Badge variant="outline" className={`${isDarkMode ? 'border-indigo-500/30 text-indigo-400' : 'border-indigo-600 text-indigo-600'}`}>
                  <Clipboard className="w-3 h-3 mr-1" />
                  {getAssessmentTypeLabel(assessmentType)}
                </Badge>
              )}
              {vuln.cve_id && (
                <Badge variant="outline" className={`${isDarkMode ? 'border-cyan-500/30 text-cyan-400' : 'border-cyan-600 text-cyan-600'} font-mono`}>
                  {vuln.cve_id}
                </Badge>
              )}
              {vuln.cwe && (
                <Badge variant="outline" className={`${isDarkMode ? 'border-purple-500/30 text-purple-400' : 'border-purple-600 text-purple-600'} font-mono`}>
                  {vuln.cwe}
                </Badge>
              )}
              {vuln.priority && (
                <Badge className={`${
                  vuln.priority === 'P0' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                  vuln.priority === 'P1' ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' :
                  vuln.priority === 'P2' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                  'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'
                } border text-sm font-mono`}>
                  {vuln.priority}
                </Badge>
              )}
            </div>
            <h2 className={`font-inter font-bold text-xl lg:text-2xl mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {vuln.title}
            </h2>
          </div>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-5">
          {/* CVSS Score */}
          <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-zinc-900/50 border border-zinc-800' : 'bg-slate-50 border border-slate-200'}`}>
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-4 h-4 text-red-400" />
              <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>CVSS Score</span>
            </div>
            <p className={`text-xl font-bold font-mono ${
              (vuln.cvss_score || 0) >= 9 ? 'text-red-400' :
              (vuln.cvss_score || 0) >= 7 ? 'text-orange-400' :
              (vuln.cvss_score || 0) >= 4 ? 'text-yellow-400' : 'text-green-400'
            }`}>
              {vuln.cvss_score ? vuln.cvss_score.toFixed(1) : 'N/A'}
            </p>
          </div>

          {/* EPSS Score */}
          <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-zinc-900/50 border border-zinc-800' : 'bg-slate-50 border border-slate-200'}`}>
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-orange-400" />
              <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>EPSS Score</span>
            </div>
            <p className={`text-xl font-bold font-mono ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
              {vuln.epss_score ? `${(vuln.epss_score * 100).toFixed(1)}%` : 'N/A'}
            </p>
          </div>

          {/* Risk Score */}
          <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-zinc-900/50 border border-zinc-800' : 'bg-slate-50 border border-slate-200'}`}>
            <div className="flex items-center gap-2 mb-1">
              <Target className="w-4 h-4 text-purple-400" />
              <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Risk Score</span>
            </div>
            <p className={`text-xl font-bold font-mono ${
              (vuln.risk_score || 0) >= 80 ? 'text-red-400' :
              (vuln.risk_score || 0) >= 60 ? 'text-orange-400' :
              (vuln.risk_score || 0) >= 40 ? 'text-yellow-400' : 'text-green-400'
            }`}>
              {vuln.risk_score ? vuln.risk_score.toFixed(1) : 'N/A'}
            </p>
          </div>

          {/* ALE / Financial Impact */}
          <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-zinc-900/50 border border-zinc-800' : 'bg-slate-50 border border-slate-200'}`}>
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-amber-400" />
              <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                {vuln.status === 'closed' ? 'Savings' : 'Annual Loss'}
              </span>
            </div>
            <p className={`text-xl font-bold font-mono ${
              vuln.status === 'closed' ? 'text-green-400' : 
              isDarkMode ? 'text-amber-400' : 'text-amber-600'
            }`}>
              {vuln.status === 'closed' 
                ? formatCurrency(vuln.ale_data?.savings || vuln.ale_data?.previous_ale)
                : formatCurrency(vuln.ale || vuln.ale_data?.ale)}
            </p>
            {vuln.ale_data?.risk_reduction_percentage > 0 && (
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                {vuln.ale_data.risk_reduction_percentage}% risk reduced
              </p>
            )}
          </div>

          {/* SLA Status */}
          <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-zinc-900/50 border border-zinc-800' : 'bg-slate-50 border border-slate-200'}`}>
            <div className="flex items-center gap-2 mb-1">
              <Timer className="w-4 h-4 text-cyan-400" />
              <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>SLA Status</span>
            </div>
            <p className={`text-sm font-bold ${
              slaStatus.status === 'overdue' ? 'text-red-400' :
              slaStatus.status === 'critical' ? 'text-orange-400' :
              slaStatus.status === 'fixed' ? 'text-green-400' : 'text-cyan-400'
            }`}>
              {slaStatus.status === 'fixed' ? 'Fixed' :
               slaStatus.status === 'overdue' ? `${Math.abs(slaStatus.daysRemaining)}d Overdue` :
               `${slaStatus.daysRemaining}d Left`}
            </p>
          </div>

          {/* Discovered Date */}
          <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-zinc-900/50 border border-zinc-800' : 'bg-slate-50 border border-slate-200'}`}>
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4 text-blue-400" />
              <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Discovered</span>
            </div>
            <p className={`text-sm font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              {vuln.discovered_at ? new Date(vuln.discovered_at).toLocaleDateString() : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs for Details */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-slate-100 border border-slate-200'} flex flex-wrap h-auto p-1`}>
          <TabsTrigger value="overview" className="data-[state=active]:bg-indigo-500 data-[state=active]:text-white">
            <FileText className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="technical" className="data-[state=active]:bg-indigo-500 data-[state=active]:text-white">
            <Code className="w-4 h-4 mr-2" />
            Technical Details
          </TabsTrigger>
          <TabsTrigger value="remediation" className="data-[state=active]:bg-indigo-500 data-[state=active]:text-white">
            <Wrench className="w-4 h-4 mr-2" />
            Remediation
          </TabsTrigger>
          <TabsTrigger value="references" className="data-[state=active]:bg-indigo-500 data-[state=active]:text-white">
            <Link2 className="w-4 h-4 mr-2" />
            References
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-5 space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Vulnerability Information */}
            <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200 shadow-card'}`}>
              <h3 className={`font-inter font-semibold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                <Bug className="w-5 h-5 text-red-400" />
                Vulnerability Information
              </h3>
              <div className="space-y-3">
                <InfoRow label="CVE ID" value={vuln.cve_id} isDarkMode={isDarkMode} mono copyable onCopy={copyToClipboard} />
                <InfoRow label="CWE" value={vuln.cwe} isDarkMode={isDarkMode} mono />
                <InfoRow label="Severity" value={vuln.severity?.toUpperCase()} isDarkMode={isDarkMode} badge={getSeverityColor(vuln.severity)} />
                <InfoRow label="Status" value={formatStatus(vuln.status)} isDarkMode={isDarkMode} badge={getStatusColor(vuln.status)} />
                <InfoRow label="Priority" value={vuln.priority || 'Not Set'} isDarkMode={isDarkMode} />
                <InfoRow label="CVSS Score" value={vuln.cvss_score?.toFixed(1)} isDarkMode={isDarkMode} />
                <InfoRow label="EPSS Score" value={vuln.epss_score ? `${(vuln.epss_score * 100).toFixed(1)}%` : null} isDarkMode={isDarkMode} />
                <InfoRow label="Risk Score" value={vuln.risk_score?.toFixed(1)} isDarkMode={isDarkMode} />
                <InfoRow label="Discovered" value={vuln.discovered_at ? new Date(vuln.discovered_at).toLocaleString() : null} isDarkMode={isDarkMode} />
                {vuln.is_exploited && (
                  <InfoRow label="Known Exploited" value="Yes (CISA KEV)" isDarkMode={isDarkMode} badge="bg-red-500/20 text-red-400 border-red-500/30" />
                )}
              </div>
            </div>

            {/* Asset & Assessment Information */}
            <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200 shadow-card'}`}>
              <h3 className={`font-inter font-semibold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                <Server className="w-5 h-5 text-cyan-400" />
                Asset & Assessment
              </h3>
              <div className="space-y-3">
                <InfoRow label="Asset Name" value={vuln.asset_name || 'Not Specified'} isDarkMode={isDarkMode} />
                <InfoRow label="Asset Type" value={vuln.asset_type ? vuln.asset_type.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 'Not Specified'} isDarkMode={isDarkMode} />
                <InfoRow label="Assessment Name" value={assessmentName} isDarkMode={isDarkMode} />
                <InfoRow label="Tester" value={vuln.tester || 'Not Assigned'} isDarkMode={isDarkMode} />
                <InfoRow label="Assignee" value={vuln.assignee || vuln.assigned_to || 'Unassigned'} isDarkMode={isDarkMode} />
                <InfoRow label="SLA Status" value={
                  slaStatus.status === 'fixed' ? 'Fixed' :
                  slaStatus.status === 'overdue' ? `${Math.abs(slaStatus.daysRemaining)} Days Overdue` :
                  `${slaStatus.daysRemaining} Days Remaining`
                } isDarkMode={isDarkMode} badge={
                  slaStatus.status === 'overdue' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                  slaStatus.status === 'fixed' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                  'bg-cyan-500/20 text-cyan-400 border-cyan-500/30'
                } />
                {vuln.tags && vuln.tags.length > 0 && (
                  <div className="flex items-start gap-2">
                    <span className={`text-sm w-32 shrink-0 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Tags</span>
                    <div className="flex flex-wrap gap-1">
                      {vuln.tags.map((tag, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {vuln.compliance_frameworks && vuln.compliance_frameworks.length > 0 && (
                  <div className="flex items-start gap-2">
                    <span className={`text-sm w-32 shrink-0 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Compliance</span>
                    <div className="flex flex-wrap gap-1">
                      {vuln.compliance_frameworks.map((fw, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{fw}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Detailed Description */}
          <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200 shadow-card'}`}>
            <h3 className={`font-inter font-semibold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              <FileText className="w-5 h-5 text-indigo-400" />
              Detailed Description
            </h3>
            <p className={`text-sm leading-relaxed whitespace-pre-wrap ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
              {vuln.description || 'No detailed description available for this vulnerability.'}
            </p>
            {vuln.impact && (
              <div className="mt-4 pt-4 border-t border-dashed border-zinc-700/50">
                <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  <AlertOctagon className="w-4 h-4 text-orange-400" />
                  Business Impact
                </h4>
                <p className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>{vuln.impact}</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Technical Details Tab */}
        <TabsContent value="technical" className="mt-5 space-y-5">
          {/* Scores Breakdown */}
          <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200 shadow-card'}`}>
            <h3 className={`font-inter font-semibold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              <Activity className="w-5 h-5 text-green-400" />
              Risk Score Breakdown
            </h3>
            <div className="space-y-4">
              <ScoreBar label="CVSS Score" value={vuln.cvss_score || 0} max={10} color="red" isDarkMode={isDarkMode} description="Common Vulnerability Scoring System - measures technical severity" />
              <ScoreBar label="EPSS Score" value={(vuln.epss_score || 0) * 100} max={100} color="orange" isDarkMode={isDarkMode} suffix="%" description="Exploit Prediction Scoring System - likelihood of exploitation in the wild" />
              <ScoreBar label="Risk Score" value={vuln.risk_score || 0} max={100} color="purple" isDarkMode={isDarkMode} description="Combined risk score considering CVSS, EPSS, and asset criticality" />
              {vuln.epss_percentile && (
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-900/50' : 'bg-slate-50'}`}>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>EPSS Percentile</span>
                    <span className={`text-sm font-mono font-bold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                      Top {(100 - vuln.epss_percentile).toFixed(0)}%
                    </span>
                  </div>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    This vulnerability is more likely to be exploited than {vuln.epss_percentile.toFixed(0)}% of all vulnerabilities
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* CVE Details */}
          {vuln.cve_id && (
            <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200 shadow-card'}`}>
              <h3 className={`font-inter font-semibold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                <Globe className="w-5 h-5 text-cyan-400" />
                CVE Information
              </h3>
              <div className="flex items-center gap-3 mb-3">
                <span className={`text-lg font-mono font-bold ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                  {vuln.cve_id}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(vuln.cve_id)}
                  className={isDarkMode ? 'text-zinc-400 hover:text-white' : 'text-slate-600'}
                >
                  <Copy className="w-4 h-4" />
                </Button>
                <a
                  href={`https://nvd.nist.gov/vuln/detail/${vuln.cve_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-500 hover:text-cyan-400 text-sm flex items-center gap-1"
                >
                  View on NVD <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          )}

          {/* CWE Details */}
          {vuln.cwe && (
            <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200 shadow-card'}`}>
              <h3 className={`font-inter font-semibold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                <FileWarning className="w-5 h-5 text-purple-400" />
                CWE - Common Weakness Enumeration
              </h3>
              <div className="flex items-center gap-3 mb-3">
                <span className={`text-lg font-mono font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                  {vuln.cwe}
                </span>
                <a
                  href={`https://cwe.mitre.org/data/definitions/${vuln.cwe.replace('CWE-', '')}.html`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-500 hover:text-purple-400 text-sm flex items-center gap-1"
                >
                  View on MITRE <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                CWE provides a common language for describing software security weaknesses in architecture, design, or code.
              </p>
            </div>
          )}
        </TabsContent>

        {/* Remediation Tab */}
        <TabsContent value="remediation" className="mt-5 space-y-5">
          {/* High-Level Remediation */}
          <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200 shadow-card'}`}>
            <h3 className={`font-inter font-semibold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              <CheckCircle className="w-5 h-5 text-green-400" />
              Recommended Remediation
            </h3>
            {vuln.remediation ? (
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-200'}`}>
                <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-emerald-300' : 'text-emerald-800'}`}>
                  {vuln.remediation}
                </p>
              </div>
            ) : (
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-900/50' : 'bg-slate-50'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                  No specific remediation guidance available. Use the AI Remediation feature for detailed, vulnerability-specific recommendations.
                </p>
              </div>
            )}
          </div>

          {/* Quick Remediation Steps */}
          <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200 shadow-card'}`}>
            <h3 className={`font-inter font-semibold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              <Layers className="w-5 h-5 text-indigo-400" />
              Quick Remediation Steps
            </h3>
            <div className="space-y-3">
              {getQuickRemediationSteps(vuln).map((step, index) => (
                <div key={index} className={`flex gap-3 p-3 rounded-lg ${isDarkMode ? 'bg-zinc-900/50' : 'bg-slate-50'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${isDarkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-600'} text-sm font-bold`}>
                    {index + 1}
                  </div>
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>{step}</p>
                </div>
              ))}
            </div>
          </div>

          {/* AI Remediation CTA */}
          <div className={`rounded-2xl p-6 text-center ${isDarkMode ? 'bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30' : 'bg-gradient-to-br from-emerald-50 to-cyan-50 border border-emerald-200'}`}>
            <Sparkles className={`w-10 h-10 mx-auto mb-3 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <h3 className={`font-inter font-semibold text-lg mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Need More Detailed Remediation?
            </h3>
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
              Get AI-powered, vulnerability-specific remediation guidance with code examples, verification steps, and implementation best practices.
            </p>
            <Button
              onClick={() => navigate(`/ai-remediation/${vuln.id}`)}
              className="bg-emerald-600 hover:bg-emerald-500"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Generate AI Remediation
            </Button>
          </div>
        </TabsContent>

        {/* References Tab */}
        <TabsContent value="references" className="mt-5 space-y-5">
          <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200 shadow-card'}`}>
            <h3 className={`font-inter font-semibold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              <BookOpen className="w-5 h-5 text-blue-400" />
              External References & Resources
            </h3>
            
            {references.length > 0 ? (
              <div className="space-y-3">
                {references.map((ref, index) => (
                  <a
                    key={index}
                    href={ref.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center justify-between p-3 rounded-lg transition-colors ${isDarkMode ? 'bg-zinc-900/50 hover:bg-zinc-800/50' : 'bg-slate-50 hover:bg-slate-100'}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                        <Link2 className="w-4 h-4 text-blue-500" />
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {ref.name}
                        </p>
                        <p className={`text-xs truncate max-w-md ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          {ref.url}
                        </p>
                      </div>
                    </div>
                    <ExternalLink className={`w-4 h-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                  </a>
                ))}
              </div>
            ) : (
              <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                No CVE or CWE references available for this vulnerability.
              </p>
            )}
          </div>

          {/* Quick Research Links */}
          <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200 shadow-card'}`}>
            <h3 className={`font-inter font-semibold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              <Globe className="w-5 h-5 text-cyan-400" />
              Quick Research Links
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {vuln.cve_id && (
                <>
                  <QuickLink
                    href={`https://nvd.nist.gov/vuln/detail/${vuln.cve_id}`}
                    label="NVD Database"
                    description="Official CVE entry"
                    isDarkMode={isDarkMode}
                  />
                  <QuickLink
                    href={`https://www.cvedetails.com/cve/${vuln.cve_id}/`}
                    label="CVE Details"
                    description="Detailed CVE info"
                    isDarkMode={isDarkMode}
                  />
                </>
              )}
              {vuln.cwe && (
                <QuickLink
                  href={`https://cwe.mitre.org/data/definitions/${vuln.cwe.replace('CWE-', '')}.html`}
                  label="CWE Definition"
                  description="Weakness details"
                  isDarkMode={isDarkMode}
                />
              )}
              <QuickLink
                href="https://owasp.org/www-community/vulnerabilities/"
                label="OWASP"
                description="Vulnerability guides"
                isDarkMode={isDarkMode}
              />
              <QuickLink
                href="https://attack.mitre.org/"
                label="MITRE ATT&CK"
                description="Attack techniques"
                isDarkMode={isDarkMode}
              />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Helper function to generate quick remediation steps based on vulnerability type
const getQuickRemediationSteps = (vuln) => {
  const title = (vuln.title || '').toLowerCase();
  const cwe = vuln.cwe || '';
  
  if (title.includes('sql injection') || cwe === 'CWE-89') {
    return [
      'Use parameterized queries or prepared statements instead of string concatenation',
      'Implement input validation with strict whitelisting',
      'Apply least privilege principle to database accounts',
      'Deploy a Web Application Firewall (WAF) for additional protection',
      'Conduct code review to identify other potential injection points'
    ];
  }
  
  if (title.includes('xss') || title.includes('cross-site scripting') || cwe === 'CWE-79') {
    return [
      'Implement output encoding for all user-controlled data',
      'Deploy Content Security Policy (CSP) headers',
      'Validate and sanitize all user inputs',
      'Use HttpOnly and Secure flags for cookies',
      'Review and update frontend frameworks to latest versions'
    ];
  }
  
  if (title.includes('security headers') || cwe === 'CWE-693') {
    return [
      'Add Content-Security-Policy header to prevent XSS and data injection',
      'Implement X-Frame-Options (DENY or SAMEORIGIN) to prevent clickjacking',
      'Add X-Content-Type-Options: nosniff to prevent MIME sniffing',
      'Enable Strict-Transport-Security (HSTS) for HTTPS enforcement',
      'Add Referrer-Policy and Permissions-Policy headers'
    ];
  }
  
  if (title.includes('ssl') || title.includes('tls') || title.includes('certificate') || cwe === 'CWE-295') {
    return [
      'Obtain and install a valid SSL/TLS certificate from a trusted CA',
      'Disable weak protocols (SSLv3, TLS 1.0, TLS 1.1)',
      'Configure strong cipher suites (AES-GCM, ChaCha20)',
      'Enable HSTS with appropriate max-age',
      'Set up certificate monitoring and auto-renewal'
    ];
  }
  
  if (title.includes('authentication') || cwe === 'CWE-287') {
    return [
      'Implement multi-factor authentication (MFA)',
      'Enforce strong password policies',
      'Add rate limiting and account lockout mechanisms',
      'Use secure session management with proper timeout',
      'Log and monitor authentication attempts'
    ];
  }
  
  // Default remediation steps
  return [
    'Review the vulnerability details and understand the attack vector',
    'Identify all affected components and systems',
    'Apply vendor patches or security updates if available',
    'Implement compensating controls if immediate patching is not possible',
    'Verify the fix through security testing and monitoring'
  ];
};

// Helper Components
const InfoRow = ({ label, value, isDarkMode, mono, badge, copyable, onCopy }) => (
  <div className="flex items-center gap-2">
    <span className={`text-sm w-32 shrink-0 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{label}</span>
    {badge && value ? (
      <Badge className={`${badge} text-xs border`}>{value}</Badge>
    ) : (
      <div className="flex items-center gap-2">
        <span className={`text-sm ${mono ? 'font-mono' : ''} ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          {value || 'N/A'}
        </span>
        {copyable && value && onCopy && (
          <button onClick={() => onCopy(value)} className="text-zinc-500 hover:text-white">
            <Copy className="w-3 h-3" />
          </button>
        )}
      </div>
    )}
  </div>
);

const ScoreBar = ({ label, value, max, color, isDarkMode, suffix = '', description }) => {
  const percentage = (value / max) * 100;
  const colorClasses = {
    red: isDarkMode ? 'bg-red-500' : 'bg-red-500',
    orange: isDarkMode ? 'bg-orange-500' : 'bg-orange-500',
    purple: isDarkMode ? 'bg-purple-500' : 'bg-purple-500',
    green: isDarkMode ? 'bg-green-500' : 'bg-green-500',
  };
  
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>{label}</span>
        <span className={`text-sm font-mono font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          {value.toFixed(1)}{suffix}
        </span>
      </div>
      <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-200'}`}>
        <div
          className={`h-full rounded-full transition-all ${colorClasses[color]}`}
          style={{ width: `${Math.min(100, percentage)}%` }}
        />
      </div>
      {description && (
        <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{description}</p>
      )}
    </div>
  );
};

const QuickLink = ({ href, label, description, isDarkMode }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`p-3 rounded-lg transition-colors flex items-center gap-3 ${isDarkMode ? 'bg-zinc-900/50 hover:bg-zinc-800/50' : 'bg-slate-50 hover:bg-slate-100'}`}
  >
    <ExternalLink className={`w-4 h-4 shrink-0 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
    <div>
      <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{label}</p>
      <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{description}</p>
    </div>
  </a>
);
